/**
 * Project page themes — the named themes every smart page is designed from
 * (`Page_Design/themes/<id>.json`). A page binds to one with `theme.ref`; the
 * backend writes the theme into the page on every load, save and publish.
 * Authoring only (platform host). See backend `routers/page_themes.py`.
 */

import api from './client'
import type { ColorScheme, PageTheme } from '../pages/designers/page/types'

export interface ProjectTheme {
  id: string
  name: string
  description?: string
  source?: { kind: string; url?: string }
  theme: PageTheme
  colorSchemes: ColorScheme[]
  created_at?: number
  updated_at?: number
  usage?: { pages: string[]; sites: string[] }
}

export interface BuiltinPreset {
  id: string
  label: string
  description: string
  theme: PageTheme
}

export interface ThemeOverview {
  themes: ProjectTheme[]
  presets: BuiltinPreset[]
  sites: { obj_name: string; title: string; theme: PageTheme }[]
}

const base = (project: string) => `/page-themes/${encodeURIComponent(project)}`

export async function listPageThemes(project: string): Promise<ThemeOverview> {
  const { data } = await api.get(base(project))
  return data
}

export async function createPageTheme(project: string, body: {
  name: string; description?: string; id?: string
  from_page?: string; from_preset?: string
  theme?: PageTheme; colorSchemes?: ColorScheme[]
}): Promise<ProjectTheme> {
  const { data } = await api.post(base(project), body)
  return data.theme
}

/** Change a theme; every page bound to it is re-themed on the server. */
export async function updatePageTheme(project: string, id: string, patch: {
  name?: string; description?: string; theme?: PageTheme; replace?: boolean
  colorSchemes?: ColorScheme[] | { upsert?: ColorScheme[]; remove?: string[] }
}): Promise<{ theme: ProjectTheme; synced: string[] }> {
  const { data } = await api.put(`${base(project)}/${encodeURIComponent(id)}`, patch)
  return data
}

export async function deletePageTheme(project: string, id: string, detach = false) {
  const { data } = await api.delete(`${base(project)}/${encodeURIComponent(id)}`, { params: { detach } })
  return data as { deleted: boolean; detached: string[]; sites: string[] }
}

/** Make a theme (project theme id or built-in preset id) the look of a
 *  website and/or pages. */
export async function applyPageTheme(project: string, body: {
  theme: string; site?: string; pages?: string[]; keep_local?: boolean
}) {
  const { data } = await api.post(`${base(project)}/apply`, body)
  return data as { theme: string; kind: 'project' | 'preset'; site?: string; pages: string[]; missing: string[] }
}
